import React from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { buildWikiUrl } from '../utils/wikiUrlParser';
import { resolveApiSource } from '../utils/wikiSourceResolver';
import { useWikiTypes } from '../contexts/WikiTypesContext';

/**
 * Props for WikiItemLink component
 */
export interface WikiItemLinkProps extends Omit<LinkProps, 'to'> {
  /** Item with hierarchy fields for URL generation */
  item: {
    id: number;
    // Source field (direct source specification)
    source?: string;
    // Category fields (universal)
    wikiCategoryId?: number | null;
    wikiSubCategoryId?: number | null;
    // Type fields (for weapons/armors/items)
    wikiTypeId?: number | null;
    wikiSubTypeId?: number | null;
    // Skills fields (for skills)
    raceId?: number | null;
    skillTypeId?: number | null;
    classId?: number | null;
    professionId?: number | null;
    // Skill ID (indicates this is a skill item)
    skill_id?: number;
    // Legacy field for source detection
    itemType?: string;
  };
  children: React.ReactNode;
}

/**
 * Build source chain from item hierarchy and resolve to API endpoint
 * Uses resolveApiSource to traverse from deepest level up
 */
function getApiSource(item: WikiItemLinkProps['item'], typesResponse: any): string {
  const sourceChain: (string | undefined)[] = [];

  // 1. Если есть skill_id - это скилл (проверяем первым, так как это однозначный индикатор)
  if (item.skill_id) {
    return 'skills';
  }

  // 2. Если есть прямое поле source в объекте - используем его
  if (item.source) {
    return resolveApiSource([item.source]);
  }

  // 3. Строим цепочку source из иерархии категорий
  if (item.wikiCategoryId) {
    const category = typesResponse.categories?.find((c: any) => c.id === item.wikiCategoryId);
    if (category) {
      sourceChain.push(category.source);

      // Для subCategory
      if (item.wikiSubCategoryId && category.subCategories) {
        const subCategory = category.subCategories.find((sc: any) => sc.id === item.wikiSubCategoryId);
        if (subCategory) {
          sourceChain.push(subCategory.source);

          // Для type
          if (item.wikiTypeId && subCategory.types) {
            const type = subCategory.types.find((t: any) => t.id === item.wikiTypeId);
            if (type) {
              sourceChain.push(type.source);

              // Для subType
              if (item.wikiSubTypeId && type.subTypes) {
                const subType = type.subTypes.find((st: any) => st.id === item.wikiSubTypeId);
                if (subType) {
                  sourceChain.push(subType.source);
                }
              }
            }
          }
        }
      }

      // Для races (skills)
      if (item.raceId && category.races) {
        const race = category.races.find((r: any) => r.id === item.raceId);
        if (race) {
          sourceChain.push(race.source);

          if (item.skillTypeId && race.skillTypes) {
            const skillType = race.skillTypes.find((st: any) => st.id === item.skillTypeId);
            if (skillType) {
              sourceChain.push(skillType.source);

              if (item.classId && skillType.classes) {
                const classItem = skillType.classes.find((c: any) => c.id === item.classId);
                if (classItem) {
                  sourceChain.push(classItem.source);

                  if (item.professionId && classItem.professions) {
                    const profession = classItem.professions.find((p: any) => p.id === item.professionId);
                    if (profession) {
                      sourceChain.push(profession.source);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  // 4. Fallback на itemType если нет данных в иерархии
  if (sourceChain.length === 0 && item.itemType) {
    sourceChain.push(item.itemType);
  }

  // 5. Резолвим через resolveApiSource
  return resolveApiSource(sourceChain);
}

/**
 * Simple wiki item link - auto-detects source from hierarchy
 */
export const WikiItemLink: React.FC<WikiItemLinkProps> = ({
  item,
  children,
  ...linkProps
}) => {
  const { categories } = useWikiTypes();

  // Resolve API source from item hierarchy
  const source = getApiSource(item, { categories });

  const url = buildWikiUrl({
    source,
    // Передаем ВСЕ поля - buildWikiUrl сам решит какие использовать
    typeId: item.wikiTypeId || undefined,
    subtypeId: item.wikiSubTypeId || undefined,
    subcategoryId: item.wikiSubCategoryId || undefined,
    raceId: item.raceId || undefined,
    skillTypeId: item.skillTypeId || undefined,
    classId: item.classId || undefined,
    professionId: item.professionId || undefined,
    itemId: item.id
  });

  return (
    <Link to={url} {...linkProps}>
      {children}
    </Link>
  );
};

export default WikiItemLink;